import { useCallback, useEffect, useMemo, useState } from 'react';

import { userService } from '@chartai/firebase';
import { type LimitsTier } from '@chartai/types';
import { nextPlanUp, shouldNudgeUpgrade } from '@chartai/utils';

import { useUserLimits } from './useUserLimits';

/** The single preferences document this hook owns. */
const PREFERENCE_ID = 'usageNudge';

interface UsageNudgeResult {
  /** Render the nudge? False while loading, and false once dismissed. */
  isVisible: boolean;
  /** The plan to name in the copy. Null means there is nowhere to upgrade to. */
  nextPlan: LimitsTier | null;
  usedMinutes: number;
  /** Null when the plan is unlimited — in which case isVisible is false anyway. */
  quotaMinutes: number | null;
  /** Dismiss for the CURRENT period only. */
  dismiss: () => void;
}

/**
 * CAB-606 — the "you're approaching your monthly limit" nudge.
 *
 * Two decisions are worth knowing about.
 *
 * **Dismissal is stored in `users/{uid}/preferences/usageNudge`, not on the
 * limits document.** The limits document is `allow write: if false`, so a user
 * could never dismiss anything kept there.
 *
 * **It is keyed by `periodStart`, not a boolean.** A plain `dismissed: true`
 * would silence the nudge permanently after one click — including next month,
 * when the user is approaching their cap all over again and the whole point is
 * to warn them. Storing which period was dismissed means the nudge returns on
 * its own when the period rolls, with nothing to reset.
 */
export function useUsageNudge(uid: string | null): UsageNudgeResult {
  const { limits, usedMinutes, isLoading } = useUserLimits(uid);
  const [dismissedPeriod, setDismissedPeriod] = useState<string | null>(null);
  const [prefLoading, setPrefLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setDismissedPeriod(null);
      setPrefLoading(false);
      return;
    }

    setPrefLoading(true);
    const unsubscribe = userService.subscribeToUserPreference(uid, PREFERENCE_ID, (data) => {
      const value = data?.['dismissedPeriodStart'];
      setDismissedPeriod(typeof value === 'string' ? value : null);
      setPrefLoading(false);
    });

    return unsubscribe;
  }, [uid]);

  // The period this dismissal would apply to. Null when there is no document
  // yet, in which case the nudge cannot fire anyway.
  const periodKey = useMemo(
    () => (limits.periodStart ? limits.periodStart.toISOString() : null),
    [limits.periodStart],
  );

  const dismiss = useCallback(() => {
    if (!uid || periodKey === null) return;
    // Optimistic: the snapshot will confirm, but the banner should disappear on
    // click rather than after a round trip.
    setDismissedPeriod(periodKey);
    void userService.setUserPreference(uid, PREFERENCE_ID, {
      dismissedPeriodStart: periodKey,
    });
  }, [uid, periodKey]);

  return useMemo(() => {
    const loading = isLoading || prefLoading;
    const dismissedThisPeriod = periodKey !== null && dismissedPeriod === periodKey;
    const isVisible = !loading && !dismissedThisPeriod && shouldNudgeUpgrade(limits);

    return {
      isVisible,
      nextPlan: nextPlanUp(limits.tier),
      usedMinutes,
      quotaMinutes: limits.quotaMinutes,
      dismiss,
    };
  }, [limits, usedMinutes, isLoading, prefLoading, dismissedPeriod, periodKey, dismiss]);
}
